import './styles/LaneEditorForm.scss'

import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Button, Divider, Icon, Segment } from 'semantic-ui-react'
// The outer wrapper is a plain `<form>` (matching OrderForm's
// `FormContainer` pattern) — putting the form inside Semantic UI's `<Form>`
// adds the `.ui.form` ancestor class, which activates Fomantic's heavy
// `.field.error` cascade (red fills on input/label/error-label). Row layout
// comes from FormLayout's FieldGroup, the same primitive the other migrated
// forms use.

import { FieldGroup } from '../../../../components/FormFields/FormLayout.styled.js'
import { FormInput } from '../../../../components/FormFields/FormInput/FormInput.jsx'
import { FormSelect } from '../../../../components/FormFields/FormSelect/FormSelect.jsx'
import { FormTextArea } from '../../../../components/FormFields/FormTextArea/FormTextArea.jsx'
import { PlaceSelect } from '../../../../components/PlaceSelect/PlaceSelect.jsx'
import { EditorUnsavedModal } from '../../../../components/EditorUnsavedModal/EditorUnsavedModal.jsx'
import { masks } from '../../../../components/FormFields/masks.js'
import { getCommodityOptions } from '../../../../lib/config/commodityOptions.js'
import { AWARDED_LABELS } from '../../utils/carrierPrice.js'

const showCommodityForLeg = (laneType, legType) => {
  const crosstownOutGate =
    laneType === 'crosstown' && legType === 'OutGateLoaded'
  const crosstownAtLegs =
    laneType !== 'crosstown' &&
    (legType === 'AtShipper' || legType === 'AtConsignee')
  return crosstownOutGate || crosstownAtLegs
}

const buildDeliveryInstructionOptions = (laneType) => [
  { key: '', value: '', text: 'NONE' },
  { key: 'drop and hook', value: 'Drop and Hook', text: 'Drop and Hook' },
  laneType?.startsWith('pickup')
    ? { key: 'live load', value: 'Live Load', text: 'Live Load' }
    : { key: 'live unload', value: 'Live Unload', text: 'Live Unload' },
]

const showDeliveryTypeForLeg = (laneType, legType) =>
  !laneType?.match('one_way') && legType?.startsWith('At')

export function LaneEditorFormView({
  control,
  formState,
  handleSubmit,
  watchedLegs,
  legsFields,
  laneType,
  marketUid,
  adminLabel,
  carrierUid,
  isAdmin,
  setLegCustomAddress,
  handleTypeChange,
  handlePlaceSelect,
  onCancel,
  isSubmitting,
  options,
}) {
  const commodityOptions = useMemo(() => getCommodityOptions('dn'), [])
  const deliveryInstructionOptions = useMemo(
    () => buildDeliveryInstructionOptions(laneType),
    [laneType],
  )
  // Same label set the list cell and the Carrier Price sort accessor use
  // (utils/carrierPrice.js). It decides which price field this form writes to,
  // so a copy that drifts from theirs puts a price in the field they do not
  // read — the lane would render as "Not Set".
  const isAwarded = AWARDED_LABELS.includes(adminLabel)

  const renderLeg = (legField, index) => {
    const legType = legField.type
    const showCommodity = showCommodityForLeg(laneType, legType)
    const showDeliveryType = showDeliveryTypeForLeg(laneType, legType)
    const showManualAddressFields = watchedLegs[index]?.addressMode === 'manual'

    return (
      <Segment key={legField.id} className='leg-segment'>
        <h4 className='leg-segment__title'>{legField.title}</h4>

        {showManualAddressFields ? (
          <>
            <FormInput
              name={`legs.${index}.address.name`}
              control={control}
              label='Name'
              placeholder='Name'
              required
              data-testid={`lanes-editor-leg-${index}-name`}
              data-automation-id={`leg-details-name-${index}`}
            />
            <div className='leg-segment__address-toolbar'>
              <button
                type='button'
                className='leg-segment__link-button'
                onClick={() => setLegCustomAddress(index, false)}
              >
                <Icon name='search' />
                Search for a place instead
              </button>
            </div>

            <FormInput
              name={`legs.${index}.address.street1`}
              control={control}
              label='Street 1'
              placeholder='Street 1'
              required
              data-automation-id={`leg-details-street1-${index}`}
            />
            <FormInput
              name={`legs.${index}.address.street2`}
              control={control}
              label='Street 2'
              placeholder='Street 2'
              data-automation-id={`leg-details-street2-${index}`}
            />
            <FieldGroup>
              <FormInput
                name={`legs.${index}.address.city`}
                control={control}
                label='City'
                placeholder='City'
                required
                data-automation-id={`leg-details-city-${index}`}
              />
              <FormSelect
                name={`legs.${index}.address.state`}
                control={control}
                label='State'
                placeholder='State'
                options={options.state}
                search
                required
                data-automation-id={`leg-details-state-${index}`}
              />
              <FormInput
                name={`legs.${index}.address.zip`}
                control={control}
                label='Zip'
                placeholder='Zip'
                required
                data-automation-id={`leg-details-zip-${index}`}
              />
            </FieldGroup>
          </>
        ) : (
          <PlaceSelect
            name={`legs.${index}.placeData`}
            control={control}
            legType={legType}
            marketUid={marketUid}
            onSelect={(place) => handlePlaceSelect(index, place)}
            index={index}
            lookupBy='place_id'
            required
          />
        )}

        <FieldGroup>
          <FormInput
            name={`legs.${index}.contact.phone`}
            control={control}
            label='Contact Phone'
            type='tel'
            mask={masks.phone.mask}
            maskPlaceholder={masks.phone.defaultPlaceholder}
            required
            data-automation-id={`leg-details-contact-phone-${index}`}
          />
          <FormInput
            name={`legs.${index}.contact.name`}
            control={control}
            label='Contact Name'
            placeholder='Contact Name'
            data-automation-id={`leg-details-contact-name-${index}`}
          />
          <FormInput
            name={`legs.${index}.contact.email`}
            control={control}
            label='Contact Email'
            placeholder='Contact Email'
            data-automation-id={`leg-details-contact-email-${index}`}
          />
        </FieldGroup>

        {showDeliveryType && (
          <FormSelect
            name={`legs.${index}.properties.deliveryInstructions`}
            control={control}
            label='Delivery Type'
            options={deliveryInstructionOptions}
            data-automation-id={`leg-details-delivery-instructions-${index}`}
          />
        )}

        <FormTextArea
          name={`legs.${index}.properties.specialInstructions`}
          control={control}
          label='Special Instructions'
          placeholder='Special Instructions'
          rows={3}
          data-automation-id={`leg-details-special-instructions-${index}`}
        />

        {showCommodity && (
          <FormSelect
            name={`legs.${index}.properties.commodity`}
            control={control}
            label='Commodity'
            options={commodityOptions}
            data-testid={`lanes-editor-leg-${index}-commodity`}
            data-automation-id={`lane-commodity-${index}`}
          />
        )}
      </Segment>
    )
  }

  return (
    <form className='lane-editor-form' onSubmit={handleSubmit} noValidate>
      <FieldGroup>
        {isAdmin && (
          <FormSelect
            name='imcCompanyUid'
            control={control}
            label='Company'
            placeholder='Company'
            options={options.imc.options || []}
            loading={options.imc.loading}
            search
            onSearchChange={(query) => options.imc.onSearchChange(query)}
            selectOnBlur={false}
            required
            data-testid='lanes-editor-company-select'
          />
        )}
        <FormSelect
          name='marketUid'
          control={control}
          label='Market'
          placeholder='Select Market'
          options={options.market}
          loading={options.market.length === 0}
          required
          data-testid='lanes-editor-market-select'
          data-automation-id='lane-market'
        />
        <FormSelect
          name='type'
          control={control}
          label='Type'
          options={options.type}
          required
          onChange={(value) => handleTypeChange(value)}
          data-testid='lanes-editor-type-select'
        />
      </FieldGroup>

      {legsFields.map((legField, index) => renderLeg(legField, index))}

      <FieldGroup>
        <FormInput
          name='name'
          control={control}
          label='Lane Name'
          placeholder='Lane Name'
          required
          data-testid='lanes-editor-name-input'
          data-automation-id='lane-name'
        />
        <FormInput
          name='imcPrice'
          control={control}
          label='Price'
          mask={masks.currency.mask}
          required
          maskPlaceholder={masks.currency.defaultPlaceholder}
          data-testid='lanes-editor-price-input'
          data-automation-id='lane-price'
        />
        <FormInput
          name='fuelSurchargePercent'
          control={control}
          label='Fuel Surcharge Percentage'
          mask={masks.percent.mask}
          maskPlaceholder={masks.percent.defaultPlaceholder}
          data-testid='lanes-editor-fsc-input'
          data-automation-id='fuel-surcharge-percentage'
        />
      </FieldGroup>

      {isAdmin && (
        <>
          <Divider section />
          <FieldGroup>
            <FormSelect
              name='administration.label'
              control={control}
              label='Label'
              placeholder='Label'
              options={options.label}
              loading={options.label.length <= 1}
              search
              data-testid='lanes-editor-label-select'
              data-automation-id='lane-label'
            />
            <FormInput
              name={
                isAwarded
                  ? 'administration.lockCarrierPrice'
                  : 'administration.carrierPrice'
              }
              control={control}
              label='Carrier Price'
              placeholder={
                isAwarded ? 'Carrier Price (locked)' : 'Carrier Price'
              }
              mask={masks.currency.mask}
              maskPlaceholder={masks.currency.defaultPlaceholder}
              disabled={!adminLabel}
              data-testid='lanes-editor-carrier-price-input'
            />
          </FieldGroup>

          {isAwarded && (
            <FormSelect
              name='administration.carrierUid'
              control={control}
              label='Carrier'
              placeholder='Select Carrier'
              options={options.carrier.options}
              loading={options.carrier.loading}
              search
              onSearchChange={(query) => options.carrier.onSearchChange(query)}
              selectOnBlur={false}
              data-testid='lanes-editor-carrier-select'
            />
          )}

          {Boolean(carrierUid) && (
            <FormSelect
              name='administration.driverUserUid'
              control={control}
              label='Driver'
              placeholder='Select Driver'
              options={options.driver.options}
              loading={options.driver.loading}
              search
              data-testid='lanes-editor-driver-select'
            />
          )}

          <FormTextArea
            name='administration.note'
            control={control}
            label='Note'
            placeholder='Enter note to attach to all orders created from this lane...'
            rows={3}
            data-testid='lanes-editor-note-input'
            data-automation-id='lane-note'
          />
        </>
      )}

      <div className='lane-editor-form__actions'>
        <Button
          type='button'
          onClick={onCancel}
          disabled={isSubmitting}
          data-testid='lanes-editor-cancel-btn'
          data-automation-id='lane-cancel-button'
        >
          Cancel
        </Button>
        <Button
          type='submit'
          primary
          loading={isSubmitting}
          disabled={isSubmitting}
          data-testid='lanes-editor-submit-btn'
          data-automation-id='lane-save-button'
        >
          <Icon name={isSubmitting ? 'spinner' : 'check'} />
          Save
        </Button>
      </div>
      <EditorUnsavedModal changes={formState.isDirty} />
    </form>
  )
}

LaneEditorFormView.propTypes = {
  control: PropTypes.object.isRequired,
  formState: PropTypes.object.isRequired,
  watchedLegs: PropTypes.array,
  handleSubmit: PropTypes.func.isRequired,
  legsFields: PropTypes.array.isRequired,
  laneType: PropTypes.string,
  marketUid: PropTypes.string,
  adminLabel: PropTypes.string,
  carrierUid: PropTypes.string,
  isAdmin: PropTypes.bool.isRequired,
  setLegCustomAddress: PropTypes.func.isRequired,
  handleTypeChange: PropTypes.func.isRequired,
  handlePlaceSelect: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  options: PropTypes.object.isRequired,
}
